// frontend/src/components/ExpenseManagement/ExpenseItem.js
// Identique à la version précédente, le composant PennylaneStatusIcon est déjà inclus.
import React, { useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Trash2, Paperclip, ChevronDown, ChevronUp, CalendarDays, User,
  Banknote, FileText, Info, AlertCircle, Edit3,
  UploadCloud, Clock, CheckCircle, EyeOff
} from 'lucide-react';
import ExpenseForm from './ExpenseForm';

const formatAmount = (amount, currency = '€') => {
    if (amount == null || isNaN(Number(amount))) return '-';
    return `${Number(amount).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
};

const openAttachment = (filePath, localApiBaseUrl) => { 
    if (!filePath || !localApiBaseUrl) return;
    const filename = filePath.split('/').pop();
    window.open(`${localApiBaseUrl}/expenses/files/${filename}`, '_blank', 'noopener,noreferrer');
};
const displayAllocations = (allocations) => {
    if (!allocations || allocations.length === 0) return <i className="text-gray-400">Aucun projet</i>;
    if (allocations.length === 1) return <span className="truncate">{allocations[0].project_name || `Projet ${allocations[0].project_id}`}</span>;
    return <span className="truncate">{allocations.length} projets</span>;
};

const PennylaneStatusIcon = ({ status }) => {
    if (!status || status === 'not_applicable') {
        return null;
    }

    switch (status) {
        case 'uploaded':
            return <CheckCircle size={13} className="text-green-500" title="Justificatif synchronisé avec Pennylane" />;
        case 'failed':
            return <AlertCircle size={13} className="text-red-500" title="Échec de la synchronisation avec Pennylane" />;
        case 'pending':
            return <Clock size={13} className="text-yellow-500 animate-pulse" title="Synchronisation en attente..." />;
        case 'excluded_amazon':
            return <EyeOff size={13} className="text-slate-400" title="Document Amazon non envoyé à Pennylane" />;
        default:
            return null;
    }
};

const ExpenseItem = ({
  expense,
  projects,
  onDelete,
  onUpdate,
  isAdmin,
  currentUserId,
  apiBaseUrl 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const canEditOrDelete = isAdmin || expense.created_by_user_id === currentUserId || expense.employee_id === currentUserId;

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  };

  const handleFormSubmit = async (formDataFromChild, expenseIdFromChild) => {
    try {
      await onUpdate(expenseIdFromChild, formDataFromChild);
      setIsExpanded(false);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde depuis ExpenseItem:", error);
    }
  };

  const totalAllocatedPercentage = expense.project_allocations?.reduce((sum, a) => sum + (a.percentage || 0), 0) || 0;
  const isAllocTotalValid = Math.abs(totalAllocatedPercentage - 100) < 0.1 || totalAllocatedPercentage === 0;
  const allocationTags = (expense.project_allocations || []).map((allocation, index) => {
    const projectLabel = allocation.project_name || `Projet ${allocation.project_id}`;
    const iterationLabel = allocation.iteration_name || null;
    const rawPercentage = allocation.percentage;
    let normalizedPercentage = null;
    if (typeof rawPercentage === 'number' && Number.isFinite(rawPercentage)) {
      normalizedPercentage = Math.round(rawPercentage);
    } else if (typeof rawPercentage === 'string') {
      const trimmed = rawPercentage.trim();
      if (trimmed !== '') {
        const parsed = Number(trimmed);
        if (Number.isFinite(parsed)) {
          normalizedPercentage = Math.round(parsed);
        }
      }
    }
    return {
      key: `${allocation.project_id || 'none'}-${allocation.iteration_id || 'none'}-${index}`,
      projectLabel,
      iterationLabel,
      percentage: normalizedPercentage,
    };
  });

  return (
    <div className={`bg-white rounded-lg shadow-sm border ${isExpanded ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-200 hover:border-gray-300'} overflow-hidden`}>
      <div className="p-2.5 flex items-center cursor-pointer hover:bg-gray-50 transition-colors" onClick={canEditOrDelete ? toggleExpansion : undefined}>
        <div className="mr-2 text-gray-400 flex-shrink-0 w-5 text-center">
            {canEditOrDelete ? (
                isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />
            ) : (
                <div className="w-5 h-[15px]"></div>
            )}
        </div>
        <div className="w-16 text-xs text-gray-600 flex items-center" title="Date">
            <CalendarDays className="w-3 h-3 mr-1 text-gray-400 inline-block" />
            {expense.date ? format(new Date(expense.date), 'dd/MM/yy') : '-'}
        </div>
        <div className="w-24 text-sm font-semibold text-gray-800 text-right pr-2" title="Montant HT">
            {formatAmount(expense.amount, expense.currency)}
        </div>
        <div className="flex-1 min-w-0 mx-1.5 text-xs text-gray-700 truncate" title={`${expense.description || ''}${expense.description && expense.vendor ? ' - ' : ''}${expense.vendor || ''}`}>
            {expense.description || expense.vendor || <i className="text-gray-400">Sans description/fournisseur</i>}
        </div>
        <div className="w-28 text-xs text-gray-500 truncate hidden md:flex items-center" title={expense.project_allocations?.map(p => p.project_name || `Projet ${p.project_id}`).join(', ')}>
            <FileText className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
            <span className="truncate">{displayAllocations(expense.project_allocations)}</span>
            {!isAllocTotalValid && expense.project_allocations?.length > 0 && (
                <AlertCircle className="w-3 h-3 ml-0.5 text-red-400 flex-shrink-0" title={`Allocations: ${totalAllocatedPercentage.toFixed(1)}%`} />
            )}
        </div>
        
        <div className="w-24 text-xs text-gray-500 truncate hidden lg:flex items-center" title={`Dépensé pour: ${expense.employee_name}`}>
            <User className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
            <span className="truncate">{expense.employee_name.split(' ')[0]}</span>
        </div>

        {(isAdmin || (expense.created_by_user_id && expense.created_by_user_id !== expense.employee_id)) && expense.created_by_user_name && (
            <div className="w-24 text-xs text-blue-500 truncate hidden lg:flex items-center" title={`Ajouté par: ${expense.created_by_user_name}`}>
                <Edit3 className="w-3 h-3 mr-1 text-blue-400 flex-shrink-0" />
                <span className="truncate">{expense.created_by_user_name.split(' ')[0]}</span>
            </div>
        )}

        <div className="flex items-center flex-shrink-0 ml-1.5 space-x-1.5">
            {expense.file_path && (
              <PennylaneStatusIcon status={expense.pennylane_status} />
            )}
            {expense.file_path && (
                <button onClick={(e) => { e.stopPropagation(); openAttachment(expense.file_path, apiBaseUrl); }} className="p-0.5 text-blue-600 hover:text-blue-700 rounded-full hover:bg-blue-50" title="Voir justificatif">
                    <Paperclip size={13} />
                </button>
            )}
            {canEditOrDelete && (
                <button onClick={(e) => { e.stopPropagation(); onDelete(expense.id); }} className="p-0.5 text-red-500 hover:text-red-600 rounded-full hover:bg-red-50" title="Supprimer">
                    <Trash2 size={13} />
                </button>
            )}
        </div>
      </div>

      <div className="px-3 pb-2 pt-2 border-t border-gray-100 bg-gray-50">
        <div className="flex flex-wrap gap-2">
          {allocationTags.length > 0 ? (
            allocationTags.map((tag) => (
              <span key={tag.key} className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-gray-200 bg-white text-[11px] text-gray-600 shadow-sm">
                <span className="font-medium text-gray-700 truncate max-w-[160px]">{tag.projectLabel}</span>
                {tag.iterationLabel && <span className="text-gray-400 truncate max-w-[140px]">• {tag.iterationLabel}</span>}
                {tag.percentage !== null && <span className="text-gray-400">{tag.percentage}%</span>}
              </span>
            ))
          ) : (
            <span className="text-[11px] italic text-gray-400">Aucune allocation projet</span>
          )}
        </div>
      </div>

      {isExpanded && canEditOrDelete && (
        <div className={`border-t border-gray-200 transition-all duration-300 ease-in-out overflow-hidden`}>
          <ExpenseForm
            key={`edit-form-${expense.id}-${isExpanded}`}
            initialData={expense}
            onSubmit={handleFormSubmit}
            projects={projects}
            employeeId={currentUserId}
            onCancel={toggleExpansion}
            apiBaseUrl={apiBaseUrl} 
          />
        </div>
      )}
    </div>
  );
};

export default ExpenseItem;
